// Dashboard Inducción — lenguaje formativo, sin alerta operacional.
import React, { useEffect, useState } from 'react';
import { GraduationCap, CheckCircle2, Target, Users, Layers, Award, Info } from 'lucide-react';
import { dashboardApi } from '../../../lib/dashboardApi';
import { useAuth } from '../../../context/AuthContext';

const DIM_LABEL = {
  D1: 'Salud emocional',
  D2: 'Convivencia',
  D3: 'Vulneración',
  D4: 'Crisis',
  D5: 'Conductas de riesgo',
  D6: 'Contexto socioemocional',
};

function KpiCard({ Icon, label, value, tone, bg, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-[#E5EAF5]" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-1.5 rounded-lg" style={{ background: bg }}>
          <Icon className="w-4 h-4" style={{ color: tone }} />
        </div>
      </div>
      <div className="text-2xl lg:text-3xl font-extrabold text-[#0B1B5E]">
        {value ?? '—'}
      </div>
      <div className="text-xs text-[#475472] mt-1">{label}</div>
      {sub && <div className="text-[10px] text-[#94A3B8] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function DashboardInduccion() {
  const { profile } = useAuth();
  const colegioId = profile?.colegio_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [evolucion, setEvolucion] = useState([]);

  useEffect(() => {
    let ignore = false;
    setLoading(true); setError(null);
    Promise.allSettled([
      dashboardApi.induccionKpis(colegioId),
      dashboardApi.induccionEvolucion(colegioId),
    ]).then((res) => {
      if (ignore) return;
      const [k, e] = res.map((r) => (r.status === 'fulfilled' ? r.value : null));
      const anyForbidden = res.some((r) => r.status === 'rejected' && r.reason?.status === 403);
      if (anyForbidden && !k) setError('Tu rol no tiene acceso al dashboard.');
      setKpis(k || null);
      setEvolucion(e?.sesiones || []);
      setLoading(false);
    });
    return () => { ignore = true; };
  }, [colegioId]);

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-[#FADBD8] p-6 text-sm text-[#8A1616]" data-testid="induccion-forbidden">
        {error}
      </div>
    );
  }

  const porDim = kpis?.observaciones_por_dimension || {};
  const porRol = kpis?.observaciones_por_rol || {};

  return (
    <div className="space-y-5" data-testid="dashboard-induccion">
      {/* Banner formativo */}
      <div className="bg-[#DFF6EC] border border-[#B5E8CD] rounded-2xl p-4 flex items-start gap-3" data-testid="induccion-banner">
        <div className="p-2 rounded-lg bg-white">
          <GraduationCap className="w-5 h-5 text-[#0F6C4B]" />
        </div>
        <div>
          <h2 className="font-extrabold text-[#0F6C4B]">Modo Inducción · práctica observacional</h2>
          <p className="text-sm text-[#0F6C4B]/80 leading-relaxed">
            Espacio formativo para el equipo del colegio. Aquí se mide la <strong>calidad del registro</strong> y la
            <strong> cobertura de las 6 dimensiones</strong> del modelo, sin generar alertas operativas ni afectar los datos oficiales.
          </p>
        </div>
      </div>

      {/* KPIs formativos */}
      <section aria-label="Indicadores de práctica observacional">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard testid="ind-kpi-total"
            Icon={Layers} tone="#0F6C4B" bg="#DFF6EC"
            label="Observaciones registradas" value={loading ? '…' : (kpis?.total_observaciones ?? 0)} />
          <KpiCard testid="ind-kpi-calidad"
            Icon={CheckCircle2} tone="#5346E6" bg="#EEF0FE"
            label="Calidad del registro"
            value={loading ? '…' : `${kpis?.calidad_registro_pct ?? 0}%`}
            sub={`${kpis?.registros_completos ?? 0} completos`} />
          <KpiCard testid="ind-kpi-cobertura"
            Icon={Target} tone="#8A5A00" bg="#FFF3D6"
            label="Cobertura de dimensiones"
            value={loading ? '…' : `${kpis?.cobertura_dimensiones ?? 0}/${kpis?.dimensiones_totales ?? 6}`} />
          <KpiCard testid="ind-kpi-incompletos"
            Icon={Info} tone="#8A4200" bg="#FFE4CC"
            label="Oportunidades de mejora" value={loading ? '…' : (kpis?.campos_incompletos ?? 0)}
            sub="Registros con campos por completar" />
          <KpiCard testid="ind-kpi-severos"
            Icon={Award} tone="#B91C1C" bg="#FADBD8"
            label="Intensidad alta en dominios severos"
            value={loading ? '…' : (kpis?.intensidad_alta_severos ?? 0)}
            sub="D3 · D4 · D5" />
          <KpiCard testid="ind-kpi-centinela-reconocidos"
            Icon={GraduationCap} tone="#5346E6" bg="#EEF0FE"
            label="Reconocimiento de situaciones críticas"
            value={loading ? '…' : (kpis?.eventos_centinela_reconocidos ?? 0)}
            sub="Práctica de eventos centinela" />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Observaciones por dimensión */}
        <section className="bg-white rounded-2xl border border-[#E5EAF5] p-5" data-testid="ind-por-dimension">
          <h2 className="font-extrabold text-[#0B1B5E] mb-1">Observaciones por dimensión</h2>
          <p className="text-xs text-[#475472] mb-3">Distribución de la práctica sobre las 6 dimensiones.</p>
          {Object.keys(porDim).length === 0 ? (
            <div className="py-4 text-sm text-[#475472] text-center">Aún no hay observaciones de práctica.</div>
          ) : (
            <ul className="space-y-2">
              {['D1','D2','D3','D4','D5','D6'].map((k) => (
                <li key={k} className="flex items-center gap-3">
                  <div className="w-12">
                    <div className="text-xs font-extrabold text-[#0B1B5E]">{k}</div>
                    <div className="text-[10px] text-[#475472] leading-tight">{DIM_LABEL[k]}</div>
                  </div>
                  <div className="flex-1 h-3 bg-[#F4F7FE] rounded-full overflow-hidden">
                    <div className="h-full bg-[#5346E6]" style={{
                      width: `${Math.min(100, ((porDim[k] || 0) / Math.max(1, ...Object.values(porDim))) * 100)}%`
                    }} />
                  </div>
                  <div className="w-10 text-right text-sm font-bold text-[#0B1B5E]">{porDim[k] || 0}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Observaciones por rol */}
        <section className="bg-white rounded-2xl border border-[#E5EAF5] p-5" data-testid="ind-por-rol">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-[#5346E6]" />
            <h2 className="font-extrabold text-[#0B1B5E]">Participación por rol</h2>
          </div>
          <p className="text-xs text-[#475472] mb-3">Quiénes están practicando la observación.</p>
          {Object.keys(porRol).length === 0 ? (
            <div className="py-4 text-sm text-[#475472] text-center">Aún no hay participantes registrados.</div>
          ) : (
            <ul className="space-y-2">
              {Object.entries(porRol).sort((a,b) => b[1] - a[1]).map(([rol, n]) => (
                <li key={rol} className="flex items-center justify-between py-1.5 border-b border-[#F1F3F9] last:border-0">
                  <span className="text-sm text-[#0B1B5E] capitalize">{rol.replace(/_/g, ' ')}</span>
                  <span className="text-sm font-bold text-[#5346E6]">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Evolución por sesión */}
      <section className="bg-white rounded-2xl border border-[#E5EAF5] p-5" data-testid="ind-evolucion">
        <h2 className="font-extrabold text-[#0B1B5E] mb-1">Evolución por sesión de inducción</h2>
        <p className="text-xs text-[#475472] mb-3">
          Sesiones formativas registradas y desempeño observacional acumulado.
        </p>
        {evolucion.length === 0 ? (
          <div className="py-6 text-center text-sm text-[#475472]" data-testid="ind-evolucion-empty">
            Aún no hay sesiones registradas.
            <p className="text-xs text-[#94A3B8] mt-1">
              Las sesiones de inducción agrupan las observaciones de práctica para medir progreso del equipo.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="ind-evolucion-table">
              <thead>
                <tr className="text-left text-[11px] text-[#475472]">
                  <th className="py-2 pr-3 font-bold">Sesión</th>
                  <th className="py-2 pr-3 font-bold">Estado</th>
                  <th className="py-2 pr-3 font-bold text-center">Observaciones</th>
                  <th className="py-2 pr-3 font-bold text-center">Completas</th>
                  <th className="py-2 pr-3 font-bold text-center">Dimensiones</th>
                  <th className="py-2 pr-3 font-bold text-center">Centinelas</th>
                </tr>
              </thead>
              <tbody>
                {evolucion.map((s) => (
                  <tr key={s.sesion_id} className="border-t border-[#F1F3F9]">
                    <td className="py-2.5 pr-3 text-[#0B1B5E] font-bold">{s.titulo}</td>
                    <td className="py-2.5 pr-3 text-[#475472] capitalize">{s.estado}</td>
                    <td className="py-2.5 pr-3 text-center text-[#0B1B5E]">{s.n_observaciones}</td>
                    <td className="py-2.5 pr-3 text-center text-[#0F6C4B]">{s.n_completas}</td>
                    <td className="py-2.5 pr-3 text-center text-[#5346E6]">{s.dimensiones_cubiertas}/6</td>
                    <td className="py-2.5 pr-3 text-center text-[#B91C1C]">{s.n_centinelas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Aclaración pie */}
      <div className="text-xs text-[#94A3B8] px-2 leading-relaxed" data-testid="induccion-disclaimer">
        Los datos de inducción no aparecen en los reportes oficiales, no activan alertas operativas ni protocolos, y no
        afectan los indicadores del Modo Oficial. Este espacio existe únicamente para el desarrollo formativo del equipo.
      </div>
    </div>
  );
}
